import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { paymentsApi } from '@/api/payments.api';
import type { ContributorPayoutSituation } from '@/lib/types/payments';
import { canAccessContributorWorkspace, homePathForRole } from '@/lib/auth/roles';

import { ContributorHeader } from './ContributorHeader';
import { BackOfficeSiteHeader } from './BackOfficeSiteHeader';
import { VisitorHeader } from './VisitorHeader';

export function Header() {
  const { user, isAuthenticated } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';
  const [payoutSituation, setPayoutSituation] = useState<ContributorPayoutSituation | null>(null);

  // Le solde affiché dans l'en-tête contributeur vient uniquement du backend
  // (GET /payments/contributor/:userId/situation).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isAuthenticated || !user?.id || !canAccessContributorWorkspace(user.role)) {
        if (!cancelled) setPayoutSituation(null);
        return;
      }
      try {
        const situation = await paymentsApi.getContributorPayoutSituation(Number(user.id));
        if (!cancelled) setPayoutSituation(situation);
      } catch {
        if (!cancelled) setPayoutSituation(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, user?.role]);

  // Admins, moderators and accountants only see the public site from their
  // panel: no contribution menu, just the way back to their space.
  if (isAuthenticated && user && !canAccessContributorWorkspace(user.role)) {
    return <BackOfficeSiteHeader lang={currentLang} homeHref={homePathForRole(user.role, currentLang)} />;
  }

  // A signed-in contributor gets the app header (tasks as verbs); visitors the public one.
  if (isAuthenticated) {
    return <ContributorHeader lang={currentLang} balance={payoutSituation ? payoutSituation.earnedAmount : null} />;
  }

  return <VisitorHeader lang={currentLang} />;
}
